import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Calculator, Zap, CheckCircle2 } from 'lucide-react';

interface ConversionStats {
  inputLength: number;
  outputLength: number;
  processingTime: number;
  operationsCount: number;
}

interface ConversionResult {
  binary: string;
  octal: string;
  decimal: string;
  hexadecimal: string;
  custom?: string;
  stats: Omit<ConversionStats, 'operationsCount'>;
}

type NumberBase = 2 | 8 | 10 | 16 | 'custom';

const NumberBaseConverterTool: React.FC = () => {
  const [inputNumber, setInputNumber] = useState<string>('');
  const [inputBase, setInputBase] = useState<NumberBase>(10);
  const [customInputBase, setCustomInputBase] = useState<number>(36);
  const [customOutputBase, setCustomOutputBase] = useState<number>(36);
  const [results, setResults] = useState<ConversionResult>({
    binary: '',
    octal: '',
    decimal: '',
    hexadecimal: '',
    custom: '',
    stats: {
      inputLength: 0,
      outputLength: 0,
      processingTime: 0
    }
  });
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [showCustomBases, setShowCustomBases] = useState<boolean>(false);
  const [stats, setStats] = useState<ConversionStats>({
    inputLength: 0,
    outputLength: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const isValidNumber = useCallback((num: string, base: number): boolean => {
    if (!num || num.trim() === '') return false;
    
    // Remove common prefixes
    let cleanNum = num.toLowerCase().replace(/^0x|^0b|^0o/, '');
    
    // Handle negative numbers
    if (cleanNum.startsWith('-')) {
      cleanNum = cleanNum.substring(1);
    }
    
    if (cleanNum === '') return false;
    
    const validChars = '0123456789abcdefghijklmnopqrstuvwxyz'.substring(0, base);
    
    for (const char of cleanNum) {
      if (!validChars.includes(char)) {
        return false;
      }
    }
    
    return true;
  }, []);

  const convertToDecimal = useCallback((num: string, base: number): number => {
    if (!num || num.trim() === '') return 0;
    
    // Handle common prefixes
    let cleanNum = num.toLowerCase();
    let isNegative = false;
    
    if (cleanNum.startsWith('-')) {
      isNegative = true;
      cleanNum = cleanNum.substring(1);
    }
    
    // Remove prefixes
    cleanNum = cleanNum.replace(/^0x|^0b|^0o/, '');
    
    if (cleanNum === '') return 0;
    
    let result = 0;
    const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
    
    for (let i = 0; i < cleanNum.length; i++) {
      const char = cleanNum[i];
      const digit = chars.indexOf(char);
      
      if (digit >= base) {
        throw new Error(`Invalid digit '${char}' for base ${base}`);
      }
      
      result = result * base + digit;
    }
    
    return isNegative ? -result : result;
  }, []);

  const convertFromDecimal = useCallback((num: number, base: number): string => {
    if (num === 0) return '0';
    
    const isNegative = num < 0;
    let absNum = Math.abs(Math.floor(num));
    
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    
    while (absNum > 0) {
      result = chars[absNum % base] + result;
      absNum = Math.floor(absNum / base);
    }
    
    return isNegative ? '-' + result : result;
  }, []);

  const convertNumber = useCallback((): ConversionResult => {
    const startTime = performance.now();
    
    if (!inputNumber.trim()) {
      return {
        binary: '',
        octal: '',
        decimal: '',
        hexadecimal: '',
        custom: '',
        stats: {
          inputLength: 0,
          outputLength: 0,
          processingTime: 0
        }
      };
    }
    
    try {
      const currentBase = inputBase === 'custom' ? customInputBase : inputBase;
      
      if (!isValidNumber(inputNumber, currentBase)) {
        throw new Error(`Invalid number for base ${currentBase}`);
      }
      
      const decimalValue = convertToDecimal(inputNumber, currentBase);
      
      const binary = convertFromDecimal(decimalValue, 2);
      const octal = convertFromDecimal(decimalValue, 8);
      const decimal = decimalValue.toString();
      const hexadecimal = convertFromDecimal(decimalValue, 16);
      const custom = showCustomBases ? convertFromDecimal(decimalValue, customOutputBase) : '';
      
      const endTime = performance.now();
      const totalOutputLength = binary.length + octal.length + decimal.length + hexadecimal.length + custom.length;
      
      setErrorMessage('');
      
      return {
        binary,
        octal,
        decimal,
        hexadecimal,
        custom,
        stats: {
          inputLength: inputNumber.length,
          outputLength: totalOutputLength,
          processingTime: endTime - startTime
        }
      };
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Conversion error');
      return {
        binary: '',
        octal: '',
        decimal: '',
        hexadecimal: '',
        custom: '',
        stats: {
          inputLength: inputNumber.length,
          outputLength: 0,
          processingTime: performance.now() - startTime
        }
      };
    }
  }, [inputNumber, inputBase, customInputBase, customOutputBase, showCustomBases, isValidNumber, convertToDecimal, convertFromDecimal]);

  useEffect(() => {
    const result = convertNumber();
    setResults(result);
    setStats(prev => ({
      ...result.stats,
      operationsCount: prev.operationsCount + (inputNumber.trim() ? 1 : 0)
    }));
  }, [convertNumber]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (text: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${label} copied to clipboard!`);
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const content = `Number Base Conversion Results
Input: ${inputNumber} (Base ${inputBase === 'custom' ? customInputBase : inputBase})

Binary (Base 2): ${results.binary}
Octal (Base 8): ${results.octal}
Decimal (Base 10): ${results.decimal}
Hexadecimal (Base 16): ${results.hexadecimal}
${showCustomBases ? `Custom Base ${customOutputBase}: ${results.custom}` : ''}

Conversion completed in ${results.stats.processingTime.toFixed(2)}ms
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'number-conversion.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          // Extract first number from file
          const match = result.match(/[\-]?[0-9a-fA-F]+/);
          if (match) {
            setInputNumber(match[0]);
            showNotification('Number loaded from file!');
          } else {
            showNotification('No valid number found in file');
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputNumber('');
    setResults({
      binary: '',
      octal: '',
      decimal: '',
      hexadecimal: '',
      custom: '',
      stats: {
        inputLength: 0,
        outputLength: 0,
        processingTime: 0
      }
    });
    setStats({
      inputLength: 0,
      outputLength: 0,
      processingTime: 0,
      operationsCount: 0
    });
    setErrorMessage('');
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleNumbers = (): void => {
    setInputNumber('255');
    setInputBase(10);
  };

  const loadRandomNumber = (): void => {
    const randomNum = Math.floor(Math.random() * 1000000);
    setInputNumber(randomNum.toString());
    setInputBase(10);
  };

  const swapBases = (): void => {
    if (inputBase !== 'custom' && results.decimal) {
      const currentDecimal = results.decimal;
      setInputNumber(currentDecimal);
      
      // Cycle through bases
      const bases: NumberBase[] = [2, 8, 10, 16];
      const currentIndex = bases.indexOf(inputBase);
      const nextIndex = (currentIndex + 1) % bases.length;
      setInputBase(bases[nextIndex]);
    }
  };

  const getBaseLabel = (base: NumberBase): string => {
    switch (base) {
      case 2: return 'Binary';
      case 8: return 'Octal';
      case 10: return 'Decimal';
      case 16: return 'Hexadecimal';
      case 'custom': return `Base ${customInputBase}`;
      default: return `Base ${base}`;
    }
  };

  const formatWithPrefix = (value: string, base: NumberBase): string => {
    if (!value) return '';
    
    switch (base) {
      case 2: return `0b${value}`;
      case 8: return `0o${value}`;
      case 16: return `0x${value}`;
      default: return value;
    }
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-teal-900 to-cyan-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Number Base Converter</h1>
              <p className="text-gray-300 text-sm">Convert numbers between binary, octal, decimal, hexadecimal, and custom bases</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Input Section */}
        <div className="mb-6 bg-gray-800 rounded-lg p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-end">
            {/* Number Input */}
            <div className="lg:col-span-2">
              <label className="block text-white font-medium mb-2">
                Input Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={inputNumber}
                  onChange={(e) => setInputNumber(e.target.value)}
                  placeholder="Enter a number..."
                  className="w-full bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none font-mono text-lg"
                />
                {errorMessage && (
                  <div className="absolute top-full mt-1 text-red-400 text-sm">
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Base Selection */}
            <div>
              <label className="block text-white font-medium mb-2">
                Input Base
              </label>
              <select
                value={inputBase}
                onChange={(e) => setInputBase(e.target.value as NumberBase)}
                className="w-full bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
              >
                <option value={2}>Binary (Base 2)</option>
                <option value={8}>Octal (Base 8)</option>
                <option value={10}>Decimal (Base 10)</option>
                <option value={16}>Hexadecimal (Base 16)</option>
                <option value="custom">Custom Base</option>
              </select>
            </div>
          </div>

          {/* Custom Base Inputs */}
          {inputBase === 'custom' && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-medium mb-2">
                  Custom Input Base (2-36)
                </label>
                <input
                  type="number"
                  min="2"
                  max="36"
                  value={customInputBase}
                  onChange={(e) => setCustomInputBase(Math.max(2, Math.min(36, parseInt(e.target.value) || 2)))}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                />
              </div>
            </div>
          )}

          {/* Custom Output Base */}
          <div className="mt-4 flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showCustomBases}
                onChange={(e) => setShowCustomBases(e.target.checked)}
                className="w-4 h-4 text-teal-600 bg-gray-700 border-gray-600 rounded focus:ring-teal-500"
              />
              <span className="text-white">Show custom output base</span>
            </label>
            
            {showCustomBases && (
              <div className="flex items-center space-x-2">
                <label className="text-white">Base:</label>
                <input
                  type="number"
                  min="2"
                  max="36"
                  value={customOutputBase}
                  onChange={(e) => setCustomOutputBase(Math.max(2, Math.min(36, parseInt(e.target.value) || 2)))}
                  className="w-20 bg-gray-900 text-white p-2 rounded border border-gray-600 focus:border-teal-500 outline-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Conversion Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-teal-400">{stats.inputLength}</div>
                <div className="text-gray-300 text-sm">Input Length</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-cyan-400">{stats.outputLength}</div>
                <div className="text-gray-300 text-sm">Total Output</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Conversions</div>
              </div>
            </div>
            
            {/* Base Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Supported Number Bases</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Binary (Base 2):</strong> 0-1, prefix 0b
                </div>
                <div className="text-gray-300">
                  <strong>Octal (Base 8):</strong> 0-7, prefix 0o
                </div>
                <div className="text-gray-300">
                  <strong>Decimal (Base 10):</strong> 0-9, no prefix
                </div>
                <div className="text-gray-300">
                  <strong>Hexadecimal (Base 16):</strong> 0-9, A-F, prefix 0x
                </div>
                <div className="text-gray-300">
                  <strong>Custom (Base 2-36):</strong> 0-9, A-Z
                </div>
                <div className="text-gray-300">
                  <strong>Range:</strong> Supports negative numbers
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        <div className="flex-1 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Conversion Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Binary */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Binary</h3>
                <button
                  onClick={() => copyToClipboard(formatWithPrefix(results.binary, 2), 'Binary')}
                  disabled={!results.binary}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.binary ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Base 2:</div>
                    <div>{results.binary}</div>
                    <div className="text-gray-400 text-xs mt-1">With prefix: 0b{results.binary}</div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter a number to see binary conversion</div>
                )}
              </div>
            </div>

            {/* Octal */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Octal</h3>
                <button
                  onClick={() => copyToClipboard(formatWithPrefix(results.octal, 8), 'Octal')}
                  disabled={!results.octal}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.octal ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Base 8:</div>
                    <div>{results.octal}</div>
                    <div className="text-gray-400 text-xs mt-1">With prefix: 0o{results.octal}</div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter a number to see octal conversion</div>
                )}
              </div>
            </div>

            {/* Decimal */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Decimal</h3>
                <button
                  onClick={() => copyToClipboard(results.decimal, 'Decimal')}
                  disabled={!results.decimal}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.decimal ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Base 10:</div>
                    <div>{results.decimal}</div>
                    <div className="text-gray-400 text-xs mt-1">{parseInt(results.decimal).toLocaleString()}</div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter a number to see decimal conversion</div>
                )}
              </div>
            </div>

            {/* Hexadecimal */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Hexadecimal</h3>
                <button
                  onClick={() => copyToClipboard(formatWithPrefix(results.hexadecimal, 16), 'Hexadecimal')}
                  disabled={!results.hexadecimal}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-3 rounded border min-h-[100px] font-mono text-white break-all">
                {results.hexadecimal ? (
                  <div>
                    <div className="text-gray-400 text-xs mb-1">Base 16:</div>
                    <div>{results.hexadecimal}</div>
                    <div className="text-gray-400 text-xs mt-1">With prefix: 0x{results.hexadecimal}</div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter a number to see hexadecimal conversion</div>
                )}
              </div>
            </div>
          </div>

          {/* Custom Base Result */}
          {showCustomBases && (
            <div className="mt-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">Custom Base {customOutputBase}</h3>
                  <button
                    onClick={() => copyToClipboard(results.custom || '', `Base ${customOutputBase}`)}
                    disabled={!results.custom}
                    className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="bg-gray-900 p-3 rounded border min-h-[80px] font-mono text-white break-all">
                  {results.custom ? (
                    <div>
                      <div className="text-gray-400 text-xs mb-1">Base {customOutputBase}:</div>
                      <div>{results.custom}</div>
                    </div>
                  ) : (
                    <div className="text-gray-500">Enter a number to see base {customOutputBase} conversion</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleNumbers}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample (255)
            </button>
            <button
              onClick={loadRandomNumber}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Random Number
            </button>
            <button
              onClick={swapBases}
              disabled={!results.decimal}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              Cycle Input Base
            </button>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".txt,.json,.csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm flex items-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>Upload File</span>
              </div>
            </label>
            <button
              onClick={downloadFile}
              disabled={!results.decimal}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download Results</span>
            </button>
            <button
              onClick={clearAll}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NumberBaseConverterTool;